import { Link, Typography } from '@globalmed/atlas-ui';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { FormBuilderOption } from '../FormBuilderOption';
import {
  TFormBuilderOption,
  TFormBuilderVariant,
} from '../FormBuilderOption/types';
import styles from './FormBuilderOption.module.css';

type TProps = {
  variant?: TFormBuilderVariant;
  options: TFormBuilderOption[];
  onChange: (options: TFormBuilderOption[]) => void;
};

export const FormBuilderOptions =
  /**
   * List of options for multiple/single choice fields with ability to add, remove and reorder options
   * @param {TProps} props - Component props
   * @param {TFormBuilderVariant} [props.variant] - Variant of options (checkbox or radio)
   * @param {TFormBuilderOption[]} props.options - Array of options
   * @param {Function} props.onChange - Callback when options change
   * @returns {JSX.Element} Form builder options list component
   */
  memo(({ options, variant, onChange }: TProps) => {
    const { t } = useTranslation();
    const handleOptionChange = useCallback(
      (updatedOption: TFormBuilderOption) => {
        onChange(
          options.map((opt) =>
            opt.id === updatedOption.id ? updatedOption : opt,
          ),
        );
      },
      [options, onChange],
    );

    const handleDownMove = useCallback(
      (option: TFormBuilderOption) => {
        const index = options.findIndex((opt) => opt.id === option.id);
        if (index < 0 || index === options.length - 1) return;

        const updated = [...options];
        [updated[index], updated[index + 1]] = [
          updated[index + 1],
          updated[index],
        ];
        onChange(updated);
      },
      [options, onChange],
    );

    const handleUpMove = useCallback(
      (option: TFormBuilderOption) => {
        const index = options.findIndex((opt) => opt.id === option.id);
        if (index <= 0) return;

        const updated = [...options];
        [updated[index], updated[index - 1]] = [
          updated[index - 1],
          updated[index],
        ];
        onChange(updated);
      },
      [options, onChange],
    );

    const handleAddOption = useCallback(() => {
      const newOption: TFormBuilderOption = {
        id: uuidv4(),
        value: '',
      };
      onChange([...options, newOption]);
    }, [options, onChange]);

    const handleRemoveOption = useCallback(
      (option: TFormBuilderOption) =>
        onChange(options.filter(({ id }) => id !== option.id)),
      [onChange, options],
    );
    return (
      <div className={styles.container}>
        <div className={styles.wrapper}>
          {!variant && (
            <Typography fontWeight={500}>
              {t('form-builder.options')}
            </Typography>
          )}
          {options.map((option) => (
            <FormBuilderOption
              key={option.id}
              option={option}
              variant={variant}
              onChange={handleOptionChange}
              onDownMove={handleDownMove}
              onUpMove={handleUpMove}
              onRemove={options.length > 2 ? handleRemoveOption : undefined}
            />
          ))}
        </div>
        <Link
          text={t('form-builder.add-option')}
          preIcon="Plus"
          sx={{ fontWeight: 600 }}
          onClick={handleAddOption}
        />
      </div>
    );
  });

FormBuilderOptions.displayName = 'FormBuilderOptions';
